import React from 'react';
import { Phone, Mail, MapPin, Clock, ArrowUp, Instagram } from 'lucide-react';
import { companyInfo, zones } from '../data/mock';

// TikTok icon (pas dans lucide-react)
const TikTokIcon = () => (
  <svg viewBox="0 0 24 24" fill="currentColor" className="w-[18px] h-[18px]">
    <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z"/>
  </svg>
);

const Footer = () => {
  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const servicesLinks = [
    { name: "Débouchage Canalisation", href: "#services" },
    { name: "Curage Haute Pression", href: "#services" },
    { name: "Inspection Caméra", href: "#services" },
    { name: "Entretien Bac à Graisse", href: "#services" },
    { name: "Pompe de Relevage", href: "#services" },
  ];

  const quickLinks = [
    { name: "Accueil", href: "#" },
    { name: "Nos Services", href: "#services" },
    { name: "Tarifs", href: "#tarifs" },
    { name: "Avis Clients", href: "#avis" },
    { name: "Zone d'intervention", href: "#zone" },
    { name: "Contact", href: "#contact" },
  ];

  return (
    <footer className="bg-gradient-to-b from-slate-900 to-slate-950 text-white">
      {/* Main Footer */}
      <div className="max-w-7xl mx-auto px-4 py-16">
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-10">
          {/* Company Info */}
          <div>
            <div className="flex items-center gap-3 mb-6">
              <img 
                src={companyInfo.logo} 
                alt={companyInfo.name}
                className="h-14 w-auto object-contain bg-white rounded-lg p-1"
              />
            </div>
            <p className="text-slate-400 mb-6 leading-relaxed">
              Spécialistes du débouchage et de l'assainissement sur Cannes et les Alpes-Maritimes. 
              Intervention rapide 24h/24, 7j/7.
            </p>
            <div className="flex gap-3">
              <a 
                href={companyInfo.social?.instagram || "#"} 
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 bg-white/10 hover:bg-gradient-to-br hover:from-purple-600 hover:to-pink-500 rounded-lg flex items-center justify-center transition-all duration-300"
                aria-label="Instagram"
              >
                <Instagram size={18} />
              </a>
              <a 
                href={companyInfo.social?.tiktok || "#"} 
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 bg-white/10 hover:bg-black rounded-lg flex items-center justify-center transition-colors duration-300"
                aria-label="TikTok"
              >
                <TikTokIcon />
              </a>
            </div>
          </div>

          {/* Services */}
          <div>
            <h4 className="text-lg font-bold mb-6 text-white">Nos Services</h4>
            <ul className="space-y-3">
              {servicesLinks.map((link, index) => (
                <li key={index}>
                  <a 
                    href={link.href}
                    className="text-slate-400 hover:text-lime-400 transition-colors duration-300 flex items-center gap-2 py-2 min-h-[44px] text-base"
                  >
                    <span className="w-1.5 h-1.5 bg-sky-500 rounded-full" />
                    {link.name}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="text-lg font-bold mb-6 text-white">Liens Rapides</h4>
            <ul className="space-y-3">
              {quickLinks.map((link, index) => (
                <li key={index}>
                  <a 
                    href={link.href}
                    className="text-slate-400 hover:text-lime-400 transition-colors duration-300 flex items-center gap-2 py-2 min-h-[44px] text-base"
                  >
                    <span className="w-1.5 h-1.5 bg-teal-500 rounded-full" />
                    {link.name}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h4 className="text-lg font-bold mb-6 text-white">Contact</h4>
            <div className="space-y-4">
              <a 
                href={`tel:${companyInfo.phone.replace(/\s/g, '')}`}
                className="flex items-center gap-3 text-slate-400 hover:text-lime-400 transition-colors duration-300 py-2 min-h-[44px]"
              >
                <Phone size={20} className="text-lime-500 flex-shrink-0" />
                <span className="font-semibold text-white text-base">{companyInfo.phone}</span>
              </a>
              <a 
                href={`mailto:${companyInfo.email}`}
                className="flex items-center gap-3 text-slate-400 hover:text-lime-400 transition-colors duration-300 py-2 min-h-[44px]"
              >
                <Mail size={20} className="text-sky-500 flex-shrink-0" />
                <span className="break-all text-base">{companyInfo.email}</span>
              </a>
              <div className="flex items-center gap-3 text-slate-400 py-2">
                <MapPin size={20} className="text-teal-500 flex-shrink-0" />
                <span className="text-base">{companyInfo.address}</span>
              </div>
              <div className="flex items-center gap-3 text-slate-400 py-2">
                <Clock size={20} className="text-lime-500 flex-shrink-0" />
                <span className="font-semibold text-lime-400 text-base">{companyInfo.availability}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Zones */}
        <div className="mt-12 pt-8 border-t border-slate-800">
          <h4 className="text-base font-semibold text-slate-400 mb-4">Zones d'intervention :</h4>
          <p className="text-slate-500 text-base leading-relaxed">
            {zones.join(' • ')}
          </p>
        </div>

        {/* Notre Réseau National */}
        <div className="mt-8 pt-8 border-t border-slate-800">
          <h4 className="text-base font-semibold text-lime-400 mb-4">Notre Réseau National - Débouchage France :</h4>
          <div className="flex flex-wrap gap-4">
            <a 
              href="https://debouchage-bordeaux-33.fr/debouchage-canalisation/" 
              target="_blank" 
              rel="noopener noreferrer"
              className="flex items-center gap-2 bg-slate-800 hover:bg-slate-700 px-4 py-3 rounded-lg transition-colors duration-300"
            >
              <MapPin size={18} className="text-lime-500" />
              <span className="text-white font-medium">Débouchage Bordeaux (33)</span>
            </a>
          </div>
          <p className="text-slate-500 text-sm mt-4">
            Retrouvez nos services de débouchage et assainissement dans d'autres régions de France.
          </p>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-slate-800">
        <div className="max-w-7xl mx-auto px-4 py-6">
          {/* Company Legal Info */}
          <div className="text-center mb-6 pb-6 border-b border-slate-800">
            <p className="text-white font-semibold text-lg mb-1">{companyInfo.legalName}</p>
            <p className="text-slate-400 text-base">{companyInfo.address}</p>
            <p className="text-slate-500 text-base mt-1">SIRET : {companyInfo.siret}</p>
          </div>
          
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <p className="text-slate-500 text-base text-center md:text-left">
              © {new Date().getFullYear()} {companyInfo.legalName}. Tous droits réservés.
            </p>
            <div className="flex items-center gap-6">
              <a href="#" className="text-slate-500 hover:text-white transition-colors py-2 min-h-[44px] flex items-center text-base">Mentions légales</a>
              <a href="#" className="text-slate-500 hover:text-white transition-colors py-2 min-h-[44px] flex items-center text-base">Politique de confidentialité</a>
            </div>
            <button
              onClick={scrollToTop}
              className="w-12 h-12 bg-sky-600 hover:bg-sky-500 rounded-lg flex items-center justify-center transition-colors duration-300 min-w-[48px] min-h-[48px]"
              aria-label="Retour en haut"
            >
              <ArrowUp size={24} />
            </button>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;